import { useEffect, useState } from 'react';
import _ from 'lodash';
import { Day, DayExercise, ExtraExercise, GetProgramResopnse, SelectOption } from '../../lib/types/programEdit';
import { Box, Button, Collapse, Flex, FormControl, FormLabel, Heading } from '@chakra-ui/react';
import { AddIcon, ChevronDownIcon, ChevronUpIcon, CopyIcon, DeleteIcon } from '@chakra-ui/icons';
import urlConstants from '../../lib/constants/url.constants';
import useApi from '../../lib/hooks/useApi';
import useToastNotification from '../../lib/hooks/useToastNotification';
import { ApiCommand } from '../../lib/Api';
import StrongSelect from '../common/StrongSelect';
import StrongSingleSelect from '../common/StrongSingleSelect';
import StrongTextarea from '../common/StrongTextarea';
import StrongInput from '../common/StrongInput';
import StrongCheckBox from '../common/StrongCheckBox';


// normal exercise path
// program.weeks[weekIndex].days[dayIndex].exercises[exerciseIndex]
// circuit exercise path
// program.weeks[weekIndex].days[dayIndex].circuits[circuitIndex].circuitExercises[exerciseIndex]


const EXTRA_EXERCISE_OPTIONS: SelectOption[] = [
  {
    label: 'Warm-up Set',
    value: '1'
  },
  {
    label: 'Normal Set',
    value: '3'
  },
  {
    label: 'Back-off Set',
    value: '2'
  }
];

type ExercisePlanProps = {
  weekIndex: number;
  dayIndex: number;
  circuitIndex?: number;
  exerciseIndex: number;
  exercise: DayExercise;
  addExercise: (weekIndex: number, dayIndex: number, newTypeId: number, newFormats: string[]) => void;
  reassignExerciseTypeIds: (deletedExerciseTypeId: number) => void;
  program: GetProgramResopnse; // ideally define this type better if known
  updateProgram: (updatedProgram: GetProgramResopnse) => void;
  updateDays?: (val: any) => void;
  days?: Day[];
  addedExNum: number;
  isWarmup?: boolean;
};

const {
  getExercisestitle: getExercisestitleUrl,
} = urlConstants.exercises

const normalizeExerciseId = (
  value: DayExercise['exerciseId'],
): string => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    return value._id || value.id || '';
  }
  return '';
};

export const ExercisePlan = ({
  weekIndex,
  dayIndex,
  circuitIndex,
  exerciseIndex,
  exercise,
  addExercise,
  reassignExerciseTypeIds,
  program,
  updateProgram,
  days = [],
  updateDays = (val) => {},
  addedExNum,
  isWarmup = false,
}: ExercisePlanProps) => {
  const isCircuitExercise = typeof circuitIndex === 'number';
  // const [isCollapsed, setIsCollapsed] = useState(addedExNum === exercise.typeId ? false : true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [Exercisestitle, setExercisesTitle] = useState<ExerciseTitle[]>([]);
  const [selectexercise, setSelectExercise] = useState<string>('');
  const [guideLocal, setGuideLocal] = useState<string>(exercise.guide ?? '');
  const showToast = useToastNotification();

  const { loading: isGetCategoriesTitleLoading, sendRequest: getCategoriestitle } =
    useApi<ExerciseTitle[]>();

    useEffect(() => {
      fetchCategoriesTitles();
    }, []);

    useEffect(() => {
      setSelectExercise(normalizeExerciseId(exercise.exerciseId));
    }, [exercise.exerciseId]);

    useEffect(() => {
      setGuideLocal(exercise.guide ?? '');
    }, [exercise._id]);

  const fetchCategoriesTitles = () => {
    getCategoriestitle({
      callback: (data: ExerciseTitle[] | null, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        if (!data) return null;
        setExercisesTitle(data);
      },
      command: ApiCommand.GET,
      url: getExercisestitleUrl,
    });
  };

  // const { data: titles, isLoading } = useQuery('get-exercise-titles', () => fetchExerciseTitles({ filterString: '' }));
  const getExerciseList = (programData: GetProgramResopnse) => {
    if (isCircuitExercise) {
      if (isWarmup) {
        return programData.weeks[weekIndex].days[dayIndex].warmups![
          circuitIndex
        ].circuitExercises;
      }
      return programData.weeks[weekIndex].days[dayIndex].circuits[
        circuitIndex
      ].circuitExercises;
    }

    return programData.weeks[weekIndex].days[dayIndex].exercises;
  };

  const updateExercise = (weekIndex: number, dayIndex: number, exerciseIndex: number, updatedExercise: DayExercise) => {
    const updatedProgram = _.cloneDeep(program);
    getExerciseList(updatedProgram)[exerciseIndex] = updatedExercise;
    updateProgram(updatedProgram);
  };

  const duplicateExercise = (weekIndex: number, dayIndex: number, exerciseIndex: number) => {
    const originExercise = getExerciseList(program)[exerciseIndex];
    
    const newExercise = { ..._.cloneDeep(originExercise), formats: [] }; // Reset formats
    const nextTypeId = getNextTypeId();
    const updatedProgram = _.cloneDeep(program);
    newExercise.typeId = nextTypeId;
    delete newExercise._id;
    getExerciseList(updatedProgram).push(newExercise);
    updateProgram(updatedProgram);
    
  };

  const deleteExercise = (weekIndex: number, dayIndex: number, exerciseIndex: number) => {
    const updatedProgram = _.cloneDeep(program); 
    const exercises = getExerciseList(updatedProgram);
    const deletedExerciseTypeId = exercise.typeId ?? 0;
    const countSameType = exercises.filter(
      (ex) => ex.typeId === deletedExerciseTypeId
    ).length;

    exercises.splice(exerciseIndex, 1);

    if (countSameType === 1) {
      exercises.forEach((item) => {
        if (
          item.typeId !== undefined &&
          item.typeId > deletedExerciseTypeId
        ) {
          item.typeId--;
        }
      });
    }

    updateProgram(updatedProgram);
  };

  const handleAddExtraExerciseClick = () => {
    const updatedProgram = _.cloneDeep(program);
    const newExtraExercise: ExtraExercise = {
      sets: 1,
      reps: 0,
      weight: 0,
      rest: 90,
      load: 0,
      type: 1,
      per_side: false,
      custom_text: '',
    };
    getExerciseList(updatedProgram)[exerciseIndex].extra.push(newExtraExercise);
    updateProgram(updatedProgram);
  };
  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const updateExerciseDetail = (key: keyof DayExercise, value: any) => {
    const updatedExercise = { ...exercise, [key]: value };
    updateExercise(weekIndex, dayIndex, exerciseIndex, updatedExercise);
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    updateExerciseDetail(name as keyof DayExercise, value);
  };

  const handleSelectChange = (exerciseId: string) => {
    setSelectExercise(exerciseId);
    updateExerciseDetail('exerciseId' as keyof DayExercise, exerciseId);
  };

  const deleteExtraExercise = (weekIndex: number, dayIndex: number, exerciseIndex: number, extraIndex: number) => {
    const updatedProgram = _.cloneDeep(program);
    getExerciseList(updatedProgram)[exerciseIndex].extra.splice(extraIndex, 1);
    updateProgram(updatedProgram);
    
  };

  const updateExtraExerciseDetail = (index: number, key : keyof ExtraExercise, value: any) => {
    const updatedExtraExercise = [...exercise.extra];
    // updatedExtraExercise[index][key] = value;
  updatedExtraExercise[index] = {...updatedExtraExercise[index], [key]: value };

    updateExerciseDetail('extra', updatedExtraExercise);
  };

  const getNextTypeId = () => {
    let typeIds = getTypeIdList();
    const maxTypeId = Math.max(0, ...typeIds);
    return maxTypeId + 1;
  };

  const getTypeIdList = () => {
    return getExerciseList(program)
      ?.map((ex) => ex.typeId)
      ?.filter((id): id is number => typeof id === 'number') || [];
  };

  return (
    <Box
      p={4}
      bg="primary.200"
      borderRadius="md"
      boxShadow="md"
      my={4}
      className={`day-${dayIndex}`}
    >
      <Flex justify="space-between" align="center">
        <Box flex="1" mr={5}>
        <Flex alignItems="center" gap={4}>
          <Heading size="md" fontWeight="bold" whiteSpace="nowrap">
            Exercise {exerciseIndex + 1}
          </Heading>
        </Flex>
        </Box>
        <Button
          backgroundColor="primary.300"
          leftIcon={<CopyIcon mr={'-2'}/>}
          _hover={{ bg: 'primary.500' }}
          mr={2}
          onClick={() => duplicateExercise(weekIndex, dayIndex, exerciseIndex)}
        />
        <Button
          backgroundColor="primary.300"
          leftIcon={<DeleteIcon mr={'-2'}/>}
          _hover={{ bg: 'primary.500' }}
          mr={5}
          onClick={() => deleteExercise(weekIndex, dayIndex, exerciseIndex)}
        />
        <Button
          backgroundColor="primary.300"
          leftIcon={isCollapsed ? <ChevronDownIcon mr={'-2'}/> : <ChevronUpIcon mr={'-2'}/>}
          onClick={toggleCollapse}
          _hover={{ bg: 'primary.500' }}
        />
      </Flex>
      <Collapse in={!isCollapsed} animateOpacity>
        <FormControl>
          <FormLabel color='gray.600'>Exercise</FormLabel>
          <StrongSingleSelect
            options={Exercisestitle?.map(({ title, id }) => ({ label: title, value: id })) || []}
            value={
              Exercisestitle
                .filter((exercisetitle) => exercisetitle.id === selectexercise)
                .map((exercisetitle) => ({
                  label: exercisetitle.title,
                  value: exercisetitle.id,
                }))}
            onSelectChange={(exerciseId) => {
              handleSelectChange(exerciseId);
            }}
          />
        </FormControl>

        <FormControl>
          <FormLabel color='gray.600' mt={3}>Guideline</FormLabel>
          <StrongTextarea
            name="guide"
            value={guideLocal}
            onChange={(e) => setGuideLocal(e.target.value)}
            onBlur={() => updateExerciseDetail('guide', guideLocal)}
            height={'80px'}
          />
        </FormControl>
        
        {exercise?.extra?.length && exercise.extra.length > 0 ? (
            <>
              {exercise.extra.map((extra: ExtraExercise, index: number) => (
                <Box key={index} p={4} bg="teal.100" rounded="md" shadow="md" mt={4}>
                  <Flex mb={2} justify="space-between" align="center">
                    <Heading size="md" fontWeight="bold">SET {index+1}</Heading>
                    <Flex gap={3}>
                      <Button
                        backgroundColor="primary.300"
                        leftIcon={<DeleteIcon mr={'-2'}/>}
                        _hover={{ bg: 'primary.500' }}
                        mr={5}
                        onClick={() => deleteExtraExercise(weekIndex, dayIndex, exerciseIndex, index)}
                      />
                    </Flex>
                  </Flex>
                  
                  {/* <Box w="50%" mb={4}>
                    <StrongSingleSelect
                      label="Set Type"
                      options={EXTRA_EXERCISE_OPTIONS}
                      value={{
                        label: `${extra.type === 1 ? 'Warm-up Set' : extra.type === 3 ? 'Normal Set' : 'Back-off Set'}`,
                        value: String(extra.type),
                      }}
                      onChange={({ value }: SelectOption) => {
                        updateExtraExerciseDetail(index, 'type', value);
                      }}
                    />
                  </Box> */}
                <Flex justify="space-between" mb={4} mt="25px">
                  <Box w="49%">
                    <FormControl>
                      <FormLabel>Sets</FormLabel>
                      <StrongInput
                        name = 'sets'
                        placeholder='sets'
                        value={extra.sets}
                        type='number'
                        onChange={(e) => {
                          updateExtraExerciseDetail(index, e.target.name as keyof ExtraExercise, e.target.value)
                        }}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel mt={5}>Weight</FormLabel>
                      <StrongInput
                        name = 'weight'
                        placeholder='Weight'
                        type='number'
                        value={extra.weight}
                        onChange={(e) => {
                          updateExtraExerciseDetail(index, e.target.name as keyof ExtraExercise, e.target.value)
                        }}
                      />
                    </FormControl>
                  </Box>
                  <Box w="49%">
                    <FormControl>
                        <FormLabel>Reps &nbsp; <StrongCheckBox
                          isChecked={extra.per_side}
                          style={{ background: 'white', marginTop: '5px' }}                    
                          onChange={(e) => {
                            updateExtraExerciseDetail(index, 'per_side', e.target.checked);
                          }} /> Per Side.    <StrongCheckBox
                            isChecked={!!extra.custom_text}
                            style={{ background: 'white', marginTop: '5px' }}
                            onChange={(e) => {
                              updateExtraExerciseDetail(index, 'custom_text', e.target.checked ? ' ' : '');
                            }} /> Custom Text
                            
                          {!!extra.custom_text && (
                            <StrongInput
                              placeholder='e.g. AMRAP'
                              value={String(extra.custom_text ?? '').trim()}
                              onChange={(e) => {
                                updateExtraExerciseDetail(index, 'custom_text', e.target.value);
                              }}
                            />
                          )}
                        
                        </FormLabel>
                      <StrongInput
                        name = 'reps'
                        placeholder='Reps'
                        value={extra.reps}
                        type='number'
                        onChange={(e) => {
                          updateExtraExerciseDetail(index, e.target.name as keyof ExtraExercise, e.target.value)
                        }}
                        />
                        
                    </FormControl>
                    <FormControl>
                      <FormLabel mt={5}>Rest (seconds)</FormLabel>
                      <StrongInput
                        name = 'rest'
                        placeholder='Rest (seconds)'
                        type='number'
                        value={extra.rest}
                        onChange={(e) => {
                          updateExtraExerciseDetail(index, e.target.name as keyof ExtraExercise, e.target.value)
                        }}
                      />
                    </FormControl>
                  </Box>
                  </Flex>
                </Box>
              ))}
            </>
          ) : null}

        

        <Button
            colorScheme='primary'
            leftIcon={<AddIcon />}
            onClick={() => handleAddExtraExerciseClick()}
            mt= {5}
        >
            Add Set
        </Button>
      </Collapse>
    </Box>
  );
};
